/* eslint-disable camelcase */
import { useMemo } from 'react';

import InfoBox from '../../../../../components/minside/InfoBox';
import CopyVippsBadge from '../../../../../components/minside/MealsTab/CopyVippsBadge';
import MealsGroup from '../../../../../components/minside/MealsTab/MealsGroup';

import { useMealsSelection } from '../../../../../hooks/useMealsSelection';

import { formatCurrency } from '../../../../../utils/formatNumber';
import { arraysEqualAsSets, getMealSummary } from '../../../../../utils/minside/meals';

import { faArrowUpRightFromSquare, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TextInput from '../../../../../components/forms/TextInput';

// Vippsnummer for Lom Bakeri og Nordal Turistsenter – vises i betalingsinformasjonen i MealsTab
const LOM_BAKERI_VIPPSNR = '644994';
const NORDAL_VIPPSNR = '744169';

function MealsTab({
    lunsj_dager = [],
    middag_dager = [],
    maltider_allergier = '',
    loading,
    onSaveMeals
}) {
    const initialDraft = useMemo(
        () => ({
            lunsjer: lunsj_dager.filter(d => d.pameldt === 'J').map(d => d.dato),
            middager: middag_dager.filter(d => d.pameldt === 'J').map(d => d.dato),
            allergier: maltider_allergier
        }),
        [lunsj_dager, middag_dager, maltider_allergier]
    );

    const {
        draft,
        isSaving,
        feedback,
        feedbackVisible,
        feedbackRef,
        toggleDato,
        setAllergier,
        saveMeals
    } = useMealsSelection({
        initialDraft,
        onSaveMeals
    });

    const isDirty =
        !arraysEqualAsSets(draft.lunsjer, initialDraft.lunsjer) ||
        !arraysEqualAsSets(draft.middager, initialDraft.middager) ||
        draft.allergier !== initialDraft.allergier;

    const summary = useMemo(
        () => getMealSummary(lunsj_dager, middag_dager, draft),
        [lunsj_dager, middag_dager, draft]
    );

    if (loading) {
        return (
            <div className="data-loader">
                <FontAwesomeIcon icon={faSpinner} spin size="xl" />
            </div>
        );
    }

    return (
        <div className="meals-tab">
            <section className="meals-section">
                <div className="meals-section__header">
                    <div className="meals-section__header-copy">
                        <h2 className="meals-section__heading">Måltider</h2>
                        <p className="meals-section__desc">
                            Velg hvilke måltider du ønsker å bestille for hele oppholdet. Når du har
                            gjort endringer, må du lagre før bestillingene sendes inn.
                        </p>
                    </div>

                    <div className="meals-section__header-action">
                        <button
                            type="button"
                            className="btn-primary btn-small"
                            onClick={saveMeals}
                            disabled={isSaving || !isDirty}
                        >
                            {isSaving ? 'Lagrer...' : 'Lagre måltider'}
                        </button>
                    </div>
                </div>

                <div className="meals-section__feedback" ref={feedbackRef}>
                    {feedback?.message ? (
                        <div
                            className={`meals-section__feedback-inner ${
                                feedbackVisible ? 'is-visible' : 'is-hidden'
                            }`}
                        >
                            <InfoBox variant={feedback.type}>
                                <p>{feedback.message}</p>
                            </InfoBox>
                        </div>
                    ) : (
                        <InfoBox variant="info-muted">
                            <p>
                                Både lunsj og middag kan bestilles helt frem til{' '}
                                <strong>kl. 10:00 to dager før</strong>.
                            </p>
                        </InfoBox>
                    )}
                </div>
            </section>

            <div className="meals-groups">
                <MealsGroup
                    title="Lunsj"
                    desc="Velg hvilke dager du ønsker lunsjpakke fra Lom Bakeri."
                    meals={lunsj_dager}
                    checkedDates={draft.lunsjer}
                    onToggle={dato => toggleDato('lunsjer', dato)}
                    disabled={isSaving}
                />

                <MealsGroup
                    title="Middag"
                    desc="Velg hvilke dager du ønsker middag i teltet på Nordal."
                    meals={middag_dager}
                    checkedDates={draft.middager}
                    onToggle={dato => toggleDato('middager', dato)}
                    disabled={isSaving}
                />
            </div>

            <section className="meals-info-section meals-allergy-section">
                <div className="meals-info-section__header">
                    <h3 className="meals-info-section__title">Matallergier eller diettbehov</h3>
                    <p className="meals-info-section__desc">
                        Dersom du har matallergier eller spesielle diettbehov, kan du skrive det inn
                        i feltet under.
                    </p>
                </div>
                <TextInput
                    id="meals-allergier"
                    className="meals-allergy-textarea"
                    type="textarea"
                    value={draft.allergier}
                    onChange={e => setAllergier(e.target.value)}
                    disabled={isSaving}
                    placeholder="F.eks. egg, glutenallergi, laktoseintoleranse, vegetar osv."
                    maxLength={30}
                    rows={2}
                />
                <p className="meals-allergy-counter">{draft.allergier.length}/30</p>
            </section>

            <section className="meals-info-section">
                <div className="meals-info-section__header">
                    <h3 className="meals-info-section__title">Betalingsinformasjon</h3>
                    <p className="meals-info-section__desc">
                        Du betaler med Vipps direkte til riktig leverandør. Skal måltider faktureres
                        må dette avtales på forhånd via{' '}
                        <a href="/om/#kontakt" target="_blank" rel="noopener noreferrer">
                            kontaktskjemaet{' '}
                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} size="sm" />
                        </a>
                        .
                    </p>
                </div>

                {(LOM_BAKERI_VIPPSNR || NORDAL_VIPPSNR) && (
                    <div className="meals-payment-grid">
                        {LOM_BAKERI_VIPPSNR && (
                            <div className="meals-payment-card">
                                <div className="meals-payment-card__top">
                                    <p className="meals-payment-card__eyebrow">Lunsj</p>
                                    <CopyVippsBadge value={LOM_BAKERI_VIPPSNR} />
                                </div>

                                <h4 className="meals-payment-card__title">Lom Bakeri</h4>

                                <div className="meals-payment-card__rows">
                                    <div className="meals-payment-card__row">
                                        <span>Antall bestilt</span>
                                        <strong>{summary.lunsj.count} stk.</strong>
                                    </div>
                                    <div className="meals-payment-card__row">
                                        <span>Å betale</span>
                                        <strong>{formatCurrency(summary.lunsj.total)}</strong>
                                    </div>
                                </div>
                            </div>
                        )}

                        {NORDAL_VIPPSNR && (
                            <div className="meals-payment-card">
                                <div className="meals-payment-card__top">
                                    <p className="meals-payment-card__eyebrow">Middag</p>
                                    <CopyVippsBadge value={NORDAL_VIPPSNR} />
                                </div>

                                <h4 className="meals-payment-card__title">Nordal Turistsenter</h4>

                                <div className="meals-payment-card__rows">
                                    <div className="meals-payment-card__row">
                                        <span>Antall bestilt</span>
                                        <strong>{summary.middag.count} stk.</strong>
                                    </div>
                                    <div className="meals-payment-card__row">
                                        <span>Å betale</span>
                                        <strong>{formatCurrency(summary.middag.total)}</strong>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <InfoBox variant="warning">
                    <p>
                        Kvitteringen fra Vipps brukes som bevis når du henter lunsjpakke på morgenen
                        eller går inn til middag i teltet på Nordal.
                    </p>
                </InfoBox>
            </section>
        </div>
    );
}

export default MealsTab;
